<template>
  <PrimeDialog
    v-model:visible="visible"
    :modal="true"
    :closable="true"
    :class="preset.dialogClass"
    :pt="{
      footer: {
        class: 'border-t border-neutral-50',
      },
    }"
  >
    <template #header>
      <div>
        <h2 class="text-lg font-semibold text-neutral-900">
          {{ props.meta.data.Table.TableNameUser }}
        </h2>
        <p v-if="title" class="text-sm text-neutral-400">
          {{ title }}
        </p>
      </div>
    </template>

    <div v-if="isSplitLayout" class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- Left column -->
      <div class="flex flex-col gap-4">
        <div
          v-for="([sectionName, section], idx) in splitSections.left"
          :key="`left-${sectionName}-${idx}`"
          :class="preset.sectionOuterClass"
        >
          <div :class="preset.sectionHeaderRowClass">
            <h3 :class="preset.sectionTitleClass">
              {{ sectionName }}
            </h3>
          </div>

          <div
            :class="[
              preset.fieldsGridBaseClass,
              preset.sectionInnerClass(String(sectionName)),
            ]"
          >
            <div
              v-for="col in section"
              :key="col.Id"
              class="flex flex-col items-start justify-between gap-0.5"
            >
              <div :class="preset.fieldLabelClass">
                {{ col.ColumnLabel }}
              </div>
              <div :class="preset.fieldValueClass">
                <RecordGridItem :data="props.data" :col="col" :meta="meta" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right column -->
      <div class="flex flex-col gap-4">
        <div class="text-right">
          <PrimeButton
            :label="showRawXMLLabel"
            @click="showXML = !showXML"
            severity="secondary"
            icon="pi pi-code"
          />
        </div>
        <div v-if="!showXML">
          <div
            v-for="([sectionName, section], idx) in splitSections.right"
            :key="`right-${sectionName}-${idx}`"
            :class="preset.sectionOuterClass"
          >
            <div :class="preset.sectionHeaderRowClass">
              <h3 :class="preset.sectionTitleClass">
                {{ sectionName }}
              </h3>
            </div>

            <div
              :class="[
                preset.fieldsGridBaseClass,
                preset.sectionInnerClass(String(sectionName)),
              ]"
            >
              <div
                v-for="col in section"
                :key="col.Id"
                class="flex flex-col items-start justify-between gap-0.5"
              >
                <div :class="preset.fieldLabelClass">
                  {{ col.ColumnLabel }}
                </div>
                <div :class="preset.fieldValueClass">
                  <RecordGridItem :data="props.data" :col="col" :meta="meta" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else>
          <pre
            v-if="rawXmlValue"
            class="w-full max-h-[60vh] overflow-auto text-xs text-neutral-200 bg-slate-900 rounded-md p-4"
            >{{ rawXmlValue }}
          </pre>
        </div>
      </div>
    </div>

    <div v-else class="flex flex-col gap-4">
      <div
        v-for="(section, index) in sections"
        :key="index"
        :class="preset.sectionOuterClass"
      >
        <div :class="preset.sectionHeaderRowClass">
          <h3 :class="preset.sectionTitleClass">
            {{ index }}
          </h3>
        </div>

        <div
          :class="[preset.fieldsGridBaseClass, preset.sectionInnerClass(index)]"
        >
          <div
            v-for="col in section"
            :key="col.Id"
            class="flex flex-col items-start justify-between gap-0.5"
          >
            <div :class="preset.fieldLabelClass">
              {{ col.ColumnLabel }}
            </div>
            <div :class="preset.fieldValueClass">
              <RecordGridItem :data="props.data" :col="col" :meta="meta" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex flex-row w-full justify-between p-4">
        <div>
          <PrimeButton
            label="Close"
            @click="visible = false"
            severity="secondary"
          />
        </div>
        <div
          v-if="props.meta.data.Actions.length > 0"
          class="flex flex-row items-center gap-2"
        >
          <FormAction
            v-for="action in props.meta.data.Actions"
            :key="action.Id"
            :action="action"
            :data="props.data"
          />
        </div>
      </div>
    </template>
  </PrimeDialog>
</template>

<script setup lang="ts">
import { getRecordGridPreset } from "~/scad-shared/presets/card.presets";
import type { IMetaColumn, IMetaComputed } from "~/scad-shared/types/scad";

const props = withDefaults(
  defineProps<{
    meta: IMetaComputed;
    data: Indexable;
  }>(),
  {},
);

const visible = defineModel<boolean>("visible");

const tableName = computed(() => {
  return props.meta?.data?.Table?.TableName;
});

const preset = computed(() => getRecordGridPreset(tableName.value));

const evaluator = new ExpressionEvaluator();

const evaluateExpression: ComputedRef<(expression: string) => boolean> =
  computed(() => {
    return (expression: string) => {
      return evaluator.evaluateExpression(expression, props.data);
    };
  });

const showField = computed<(col: IMetaColumn) => boolean>(() => {
  return (col: IMetaColumn) => {
    const evaluationResult: boolean = col.ShowExpression
      ? evaluateExpression.value(col.ShowExpression)
      : true;

    const isRequiredResult: boolean = true;

    if (!props.data?.[col.ColumnNamePc]) {
      return false;
    }

    return evaluationResult && isRequiredResult;
  };
});

const title = computed(() => {
  return props.data?.[
    props.meta.data.Table.NameColumn ?? props.meta.primaryKeyColumnNamePc
  ];
});

const sections = computed(() => {
  const sectionsMap: { [key: string]: IMetaColumn[] } = {};
  props.meta.enabledCardColumns.forEach((col) => {
    const section = col.FormSection || "Default";
    if (!showField.value(col)) {
      return;
    }
    if (!sectionsMap[section]) {
      sectionsMap[section] = [];
    }
    sectionsMap[section].push(col);
  });
  return sectionsMap;
});

const isSplitLayout = computed(
  () => tableName.value === "quote_result_grid_view",
);

const splitLayoutConfig: Record<
  "quote_result_grid_view",
  { left: string[]; right: string[] }
> = {
  quote_result_grid_view: {
    left: ["Quote Details", "Life 1", "Life 2", "Policy Details", "Commission"],
    right: ["Provider Response", "Coverage Details", "Commission Details"],
  },
};

type SectionEntry = [string, IMetaColumn[]];

const splitSections = computed(() => {
  const entries = Object.entries(sections.value) as SectionEntry[];

  if (!isSplitLayout.value) {
    return { left: [] as SectionEntry[], right: [] as SectionEntry[] };
  }

  const cfg = splitLayoutConfig.quote_result_grid_view;
  const leftSet = new Set<string>(cfg.left);
  const rightSet = new Set<string>(cfg.right);

  const left: SectionEntry[] = [];
  const right: SectionEntry[] = [];
  const leftover: SectionEntry[] = [];

  for (const entry of entries) {
    const [name] = entry;
    if (leftSet.has(name)) {
      left.push(entry);
    } else if (rightSet.has(name)) {
      right.push(entry);
    } else {
      leftover.push(entry);
    }
  }

  return { left: [...left, ...leftover], right };
});

const showXML = ref(false);

const showRawXMLLabel = computed(() =>
  showXML.value ? "Hide Raw XML" : "Show Raw XML",
);

const rawXmlValue = computed(() => {
  const value = props.data?.["FullResponseXml"];

  if (!value) return null;

  return typeof value === "string" ? formatXml(value) : String(value);
});

function formatXml(xml: string) {
  const PADDING = "  ";
  const reg = /(>)(<)(\/*)/g;
  let formatted = "";
  let pad = 0;

  xml = xml.replace(reg, "$1\r\n$2$3");
  xml.split("\r\n").forEach((node) => {
    let indent = 0;

    if (node.match(/^<\/\w/)) {
      if (pad !== 0) pad -= 1;
    } else if (node.match(/^<\w[^>]*[^\/]>.*$/)) {
      indent = 1;
    }

    formatted += PADDING.repeat(pad) + node + "\r\n";
    pad += indent;
  });

  return formatted.trim();
}
</script>

<style scoped></style>
